import {Injectable, signal} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {HttpEvent, HttpEventType, HttpProgressEvent} from '@angular/common/http';
import {MypartHttpService} from './mypart-http.service';
import {ModalService} from './modal.service';
import {hasError, isNoMatchesError, SearchVisualsResponse, GamingResponse, hasGamingSongs} from './models/api-response.models';

@Injectable({
  providedIn: 'root'
})
export class ApiIntegrationService {

  // ============================================================================
  // STATE MANAGEMENT - EXACT preservation from LiveDemoComponent
  // ============================================================================

  private uploadProgressValue = signal(0);
  private showProgressIndicator = signal(false);
  private analyzingState = signal(false);
  private progressSimulationInterval: any = null;
  private requestIdCounter = 0;

  // Store current analysis parameters for retry logic
  private currentFiles: File[] = [];
  private currentTextPrompt?: string;
  private currentGameContext?: string;

  constructor(
    private mypartHttpService: MypartHttpService,
    private modalService: ModalService
  ) {
  }

  // ============================================================================
  // MAIN API INTEGRATION - EXACT preservation from LiveDemoComponent.analyze()
  // ============================================================================

  analyzeFiles(files: File[], textPrompt?: string, isRetry: boolean = false, gameContext?: string): Observable<HttpEvent<SearchVisualsResponse | GamingResponse>> {
    // Validate file selection
    if (files.length === 0) {
      return throwError(() => new Error('No files selected'));
    }

    // Store current parameters for potential retry
    this.currentFiles = files;
    this.currentTextPrompt = textPrompt;
    this.currentGameContext = gameContext;

    // Reset state (but keep progress if this is a retry)
    this.analyzingState.set(true);
    if (!isRetry) {
      this.uploadProgressValue.set(0);
      this.showProgressIndicator.set(false);
    }
    this.requestIdCounter++;

    const currentRequestId = this.requestIdCounter;

    // Call the appropriate API service based on gaming mode
    return new Observable(observer => {
      let apiCall: Observable<any>;

      if (gameContext) {
        // Gaming mode - use contextual search
        const file = files[0]; // Use first file for contextual search
        const isVideo = file.type.startsWith('video/');

        // Convert contextual search response to HttpEvent format
        const contextualCall = isVideo
          ? this.mypartHttpService.searchContextualVideo(file, textPrompt, gameContext)
          : this.mypartHttpService.searchContextualImage(file, textPrompt, gameContext);

        // Wrap the response in HttpEvent format
        apiCall = new Observable(observer => {
          // Send initial event
          observer.next({ type: HttpEventType.Sent } as any);

          contextualCall.subscribe({
            next: (response) => {
              observer.next({
                type: HttpEventType.Response,
                body: response
              } as any);
              observer.complete();
            },
            error: (error) => observer.error(error)
          });
        });
      } else {
        // Normal mode - use existing search
        apiCall = this.mypartHttpService.searchVisuals(
          files,
          10, // Default number of results
          'section', // Search resolution
          textPrompt
        );
      }

      const subscription = apiCall.subscribe({
        next: (event: HttpEvent<any>) => {
          // Only process if still the current request
          if (currentRequestId === this.requestIdCounter) {
            try {
              this.handleAnalysisEvent(event, isRetry, currentRequestId);
              observer.next(event);
            } catch (error) {
              // Catch errors thrown from analysis event handling (like NO_MATCHES_FOUND)
              observer.error(error);
            }
          }
        },
        error: (error) => {
          if (currentRequestId === this.requestIdCounter) {
            // Check if this is a retryable error
            if (!this.isNonRetryableError(error) && !isRetry) {
              // First attempt failed with retryable error - attempt auto-login and retry
              console.log('First attempt failed with retryable error, attempting auto-login and retry...');
              this.loginAndRetryAnalysis().subscribe({
                next: (retryEvent) => observer.next(retryEvent),
                error: (retryError) => {
                  // Retry also failed - now propagate the error
                  this.handleAnalysisError(retryError, true);
                  observer.error(retryError);
                },
                complete: () => observer.complete()
              });
            } else {
              // Non-retryable error or retry failed - handle and propagate
              this.handleAnalysisError(error, isRetry);
              observer.error(error);
            }
          }
        },
        complete: () => {
          console.log('Analysis request completed');
          if (currentRequestId === this.requestIdCounter) {
            this.analyzingState.set(false);
            this.stopSimulatedProgress();
            observer.complete();
          }
        }
      });

      // Return cleanup function
      return () => {
        subscription.unsubscribe();
        if (currentRequestId === this.requestIdCounter) {
          this.analyzingState.set(false);
          this.stopSimulatedProgress();
        }
      };
    });
  }

  // ============================================================================
  // HTTP EVENT HANDLING - EXACT preservation
  // ============================================================================

  getUploadProgress(): number {
    return this.uploadProgressValue();
  }

  getShowUploadProgress(): boolean {
    return this.showProgressIndicator();
  }

  isAnalyzing(): boolean {
    return this.analyzingState();
  }

  reset(): void {
    this.uploadProgressValue.set(0);
    this.showProgressIndicator.set(false);
    this.analyzingState.set(false);
    this.stopSimulatedProgress();
  }

  // ============================================================================
  // ERROR HANDLING - EXACT preservation
  // ============================================================================

  cleanup(): void {
    this.stopSimulatedProgress();
    this.analyzingState.set(false);
  }

  /**
   * Handles HTTP events from the analysis API call
   * EXACT preservation from LiveDemoComponent.handleAnalysisEvent()
   */
  private handleAnalysisEvent(event: HttpEvent<any>, isRetry: boolean, requestId: number): void {
    // Ignore events for stale requests
    if (requestId !== this.requestIdCounter) {
      console.log(`Ignoring event for stale request ${requestId}`);
      return;
    }

    switch (event.type) {
      case HttpEventType.Sent:
        console.log('Request sent');
        this.startSimulatedProgress();
        break;

      case HttpEventType.UploadProgress:
        this.handleUploadProgress(event);
        break;

      case HttpEventType.Response:
        console.log('API Service: HTTP Response received, completing progress');

        // ULTRA-SIMPLIFIED: Just complete progress, no response processing at all
        this.stopSimulatedProgress();
        this.uploadProgressValue.set(100);
        this.showProgressIndicator.set(false);
        this.analyzingState.set(false);
        console.log('API Service: Progress completed');
        break;
    }
  }

  /**
   * Handles upload progress events
   * EXACT preservation from LiveDemoComponent.handleUploadProgress()
   */
  private handleUploadProgress(event: HttpEvent<any>): void {
    if (event.type === HttpEventType.UploadProgress) {
      // Since HttpProgressEvent is an interface, we check for properties instead of using instanceof
      const progressEvent = event as HttpProgressEvent;
      if (progressEvent.total) {
        // Calculate and update upload percentage
        const uploadPercentage = Math.round(progressEvent.loaded / progressEvent.total * 100);
        this.uploadProgressValue.set(uploadPercentage);
        this.showProgressIndicator.set(true);
        console.log(`Upload progress: ${uploadPercentage}%`);
      }
    }
  }

  /**
   * Handles the analysis response from the API
   * Updated to handle empty results with retry logic
   */
  private handleAnalysisResponse(response: SearchVisualsResponse, isRetry: boolean): SearchVisualsResponse | null {
    // Complete response received
    console.log('Analysis results:', response);
    console.log('Entering handleAnalysisResponse, response status:', response?.status);

    // Check if response is successful and has data
    if (response && response.status === 'success') {
      console.log('Response status is success, checking for errors...');
      // Check if there's an error message within a successful response
      if (hasError(response)) {
        console.log('Response has error, handling API error');
        this.handleApiErrorInResponse(response, isRetry);
        return null;
      } else if (response.data) {
        console.log('Response has data, checking results length:', response.data.results?.length);
        // Let component handle empty results - API service just completes progress
        if (response.data.results.length === 0) {
          console.log('Received empty results array - completing progress and letting component handle retry logic');
          this.stopSimulatedProgress();
          this.uploadProgressValue.set(100);
          this.showProgressIndicator.set(false);
          this.analyzingState.set(false);
          return response; // Return response so component can process empty results
        }

        // Success with results - complete progress and stop analyzing
        console.log(`Analysis successful with ${response.data.results.length} results`);
        console.log('Completing progress: setting to 100%');

        // Force complete the progress immediately
        this.stopSimulatedProgress();
        this.uploadProgressValue.set(100);
        this.showProgressIndicator.set(false);
        this.analyzingState.set(false);

        console.log('Progress completed, current value:', this.uploadProgressValue());
        return response;
      } else {
        console.log('Response data is missing or null');
      }
    } else {
      console.log('Response is null or status is not success. Response:', response);
    }

    console.log('handleAnalysisResponse returning null');
    return null;
  }

  /**
   * Handles API errors that come within a successful HTTP response
   * Updated to follow the new flow - "no matches" triggers alternative prompts in component
   */
  private handleApiErrorInResponse(response: SearchVisualsResponse, isRetry: boolean): void {
    // Special handling for "no matches" error - will be handled at HttpEvent level
    if (isNoMatchesError(response)) {
      // This case is now handled in handleAnalysisEvent to ensure component gets response first
      return;
    } else if (!isRetry) {
      // First attempt failed - keep progress running and try to login and retry
      console.log('API error on first attempt, attempting auto-login and retry...');
      this.loginAndRetryAnalysis();
    } else {
      // Retry also failed - stop progress and show error modal
      console.log('Retry after login also failed, showing error modal');
      this.stopProgressAndAnalyzing();
      this.modalService.showHttpError({
        errorMessage: response.error?.message || 'Unknown error',
        errorDetails: JSON.stringify(response, null, 2),
        errorCode: response.error?.code || 'API Error'
      });
    }
  }

  /**
   * Handles HTTP errors from the analysis API call
   * Updated to only handle error display and cleanup (retry logic moved to main observable chain)
   */
  private handleAnalysisError(error: any, isRetry: boolean): void {
    // Extract error details
    const errorDetails = this.extractErrorDetails(error);

    // Log error for debugging
    console.error(`Analysis Failed (Code: ${errorDetails.code}). Details: ${errorDetails.message}`);
    console.error('Analysis error:', error, 'Displayed error:', errorDetails);

    // Stop progress and show appropriate error
    this.stopProgressAndAnalyzing();

    // Handle specific error cases that don't require login/retry
    if (this.isNonRetryableError(error)) {
      this.handleNonRetryableError(error);
    } else if (isRetry) {
      // Retry also failed - show error modal
      console.log('Retry after login also failed, showing error modal');
      this.modalService.showHttpError(this.createErrorData(error));
    }
    // Note: First attempt retryable errors are now handled in the main observable chain
  }

  // ============================================================================
  // PROGRESS SIMULATION - EXACT preservation with exact timing
  // ============================================================================

  /**
   * Determines if an error is non-retryable (can't be fixed by login)
   * EXACT preservation from LiveDemoComponent.isNonRetryableError()
   */
  private isNonRetryableError(error: any): boolean {
    // List of error status codes that can't be fixed by login
    const nonRetryableStatusCodes = [400, 413, 415, 422];
    return nonRetryableStatusCodes.includes(error.status);
  }

  /**
   * Handles errors that can't be fixed by login
   * EXACT preservation from LiveDemoComponent.handleNonRetryableError()
   */
  private handleNonRetryableError(error: any): void {
    switch (error.status) {
      case 400:
        // Bad request or unprocessable entity
        this.modalService.showErrorMessage({
          message: 'There was an issue with your request. Please try again or contact support.'
        });
        break;
      case 413:
        // Payload too large
        this.modalService.showErrorMessage({
          message: 'The files you selected are too large. Please try smaller files or fewer files.'
        });
        break;
      case 415:
        // Unsupported media type
        this.modalService.showErrorMessage({
          message: 'The file format is not supported. Please try a different file.'
        });
        break;
      case 422:
        this.modalService.showErrorMessage({
          message: 'Your request failed due to an inconsistency with our content guidelines. Please try again with a different file.'
        });
        break;
      default:
        // Generic error
        this.modalService.showErrorMessage({
          message: 'An unexpected error occurred. Please try again.'
        });
    }
  }

  // ============================================================================
  // PUBLIC GETTERS - For component integration
  // ============================================================================

  /**
   * Extracts detailed error information from error objects
   * EXACT preservation from LiveDemoComponent.extractErrorDetails()
   */
  private extractErrorDetails(error: any): { code: string, message: string, details: string } {
    let code = 'Unknown Error';
    let message = 'An unexpected error occurred';
    let details = '';

    if (error.status) {
      code = `HTTP ${error.status}`;

      switch (error.status) {
        case 400:
          message = 'Bad Request - Invalid data sent to server';
          details = 'The request could not be understood by the server due to malformed syntax.';
          break;
        case 401:
          message = 'Authentication required';
          details = 'You need to log in to access this resource.';
          break;
        case 403:
          message = 'Access denied';
          details = 'You do not have permission to access this resource.';
          break;
        case 404:
          message = 'Service not found';
          details = 'The requested service endpoint could not be found.';
          break;
        case 413:
          message = 'File size too large';
          details = 'One or more files exceed the maximum upload size limit.';
          break;
        case 415:
          message = 'Unsupported file type';
          details = 'The file format is not supported by the service.';
          break;
        case 422:
          message = 'Invalid file content';
          details = 'Your request failed due to an inconsistency with our content guidelines. Please try again with a different file.';
          break;
        case 500:
          message = 'Server error';
          details = 'An internal server error occurred. Please try again later.';
          break;
        case 502:
          message = 'Service unavailable';
          details = 'The service is temporarily unavailable. Please try again later.';
          break;
        case 503:
          message = 'Service overloaded';
          details = 'The service is temporarily overloaded. Please try again later.';
          break;
        default:
          message = `Server returned error ${error.status}`;
          details = error.statusText || 'No additional details available.';
      }
    }

    // Try to extract more specific error message from response body
    if (error.error && typeof error.error === 'object') {
      if (error.error.message) {
        message = error.error.message;
      }
      if (error.error.details) {
        details = error.error.details;
      }
    } else if (error.error && typeof error.error === 'string') {
      try {
        const parsed = JSON.parse(error.error);
        if (parsed.message) {
          message = parsed.message;
        }
      } catch {
        // If parsing fails, use the string as the message
        message = error.error;
      }
    }

    return {code, message, details};
  }

  /**
   * Creates formatted error data for modal display
   * EXACT preservation from LiveDemoComponent.createErrorData()
   */
  private createErrorData(error: any): { errorMessage: string, errorDetails: string, errorCode: string } {
    const details = this.extractErrorDetails(error);

    return {
      errorMessage: details.message,
      errorDetails: JSON.stringify(error, null, 2),
      errorCode: details.code
    };
  }

  /**
   * Initiates login and retry flow
   * Updated to return retry observable for component handling
   */
  private loginAndRetryAnalysis(): Observable<HttpEvent<SearchVisualsResponse | GamingResponse>> {
    console.log('Attempting auto-login...');

    // Return observable that handles login and retry
    return new Observable(observer => {
      this.mypartHttpService.login().subscribe({
        next: (response) => {
          console.log('Auto-login successful, retrying analysis...');
          // Create retry subscription and forward events to original observer
          const retrySubscription = this.analyzeFiles(this.currentFiles, this.currentTextPrompt, true, this.currentGameContext).subscribe({
            next: (event) => observer.next(event),
            error: (error) => observer.error(error),
            complete: () => observer.complete()
          });

          // Return cleanup function
          return () => retrySubscription.unsubscribe();
        },
        error: (loginError) => {
          console.error('Auto-login failed:', loginError);
          // Stop progress and show error modal
          this.stopProgressAndAnalyzing();
          this.modalService.showHttpError({
            errorMessage: 'Login failed. Please try again.',
            errorDetails: JSON.stringify(loginError, null, 2),
            errorCode: 'LOGIN_FAILED'
          });
          observer.error(loginError);
        }
      });
    });
  }

  /**
   * Stops progress bar and analyzing state
   */
  private stopProgressAndAnalyzing(): void {
    this.uploadProgressValue.set(0);
    this.showProgressIndicator.set(false);
    this.analyzingState.set(false);
    this.stopSimulatedProgress();
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Starts simulated progress animation
   * EXACT preservation from LiveDemoComponent.startSimulatedProgress()
   */
  private startSimulatedProgress(): void {
    // Stop any existing simulation
    this.stopSimulatedProgress();

    // Reset progress to ensure we start from 0
    this.uploadProgressValue.set(0);

    // Show the progress indicator
    this.showProgressIndicator.set(true);

    // Calculate interval speed based on file size (if available)
    // This makes smaller files upload "faster" in the simulation
    const intervalSpeed = 200; // Default 200ms intervals

    console.log(`Starting simulated progress with interval: ${intervalSpeed}ms`);

    // Create the interval for updating progress
    this.progressSimulationInterval = setInterval(() => {
      // Different simulation curves depending on current progress
      if (this.uploadProgressValue() < 30) {
        // Initial fast progress (0-30%)
        this.uploadProgressValue.update(prev => prev + 2 + Math.random());
      } else if (this.uploadProgressValue() < 60) {
        // Medium progress (30-60%)
        this.uploadProgressValue.update(prev => prev + 0.7 + Math.random() * 0.6);
      } else if (this.uploadProgressValue() < 85) {
        // Slower progress (60-85%)
        this.uploadProgressValue.update(prev => prev + 0.2 + Math.random() * 0.4);
      } else if (this.uploadProgressValue() < 90) {
        // Very slow progress at the end (85-90%)
        this.uploadProgressValue.update(prev => prev + 0.05 + Math.random() * 0.1);
      } else {
        // Don't go past 90% until we actually get server response
        this.uploadProgressValue.update(prev => 90 - (Math.random() * 0.5));
      }

      // Cap progress at 90% until we get server response
      if (this.uploadProgressValue() > 90) {
        this.uploadProgressValue.update(prev => 90);
      }

      // Always round to a whole number for clean display
      this.uploadProgressValue.update(prev => Math.round(prev));
    }, intervalSpeed);
  }

  /**
   * Stops any ongoing progress simulation
   * EXACT preservation from LiveDemoComponent.stopSimulatedProgress()
   */
  private stopSimulatedProgress(): void {
    if (this.progressSimulationInterval) {
      clearInterval(this.progressSimulationInterval);
      this.progressSimulationInterval = null;
      console.log('Progress simulation stopped');
    }
  }
}
